import { useEffect, useMemo, useState } from "react";
import "./ReportsSectionCompact.css";
import { useNavigate } from "react-router-dom";
import {
  exportSuperAdminTaxLiabilityDetail,
  exportSuperAdminTaxLiabilitySummary,
  getSuperAdminTaxLiabilityFilters,
  postSuperAdminTaxLiabilityDetail,
  postSuperAdminTaxLiabilitySummary,
} from "../api/superadmin";
import ModalCloseX from "../components/common/ModalCloseX";
import InfoTooltip from "../components/common/InfoTooltip";
import SortableTh from "../components/common/SortableTh";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import { useAuth } from "../context/AuthContext";
import {
  buildSuperAdminReportHelpGuide,
  getSuperAdminHelpAnchors,
} from "../constants/superAdminReportHelpGuides";
import {
  BTN_OUTLINE,
  BTN_PRIMARY,
  FIELD_CLASS,
  LABEL_TEXT,
  MODAL_TEXT,
} from "../ui/modalStyles";
import useTableSort from "../hooks/useTableSort";
import { TAX_LIABILITY_TOOLTIPS } from "../constants/reportTooltips";
import useHelpGuideNavigation from "../hooks/useHelpGuideNavigation";

const SCOPE_OPTIONS = [
  { value: "all", label: "All Salons" },
  { value: "business", label: "Business" },
  { value: "salon", label: "Salon" },
];

const SUPERADMIN_TAX_TOOLTIPS = {
  ...TAX_LIABILITY_TOOLTIPS,
  scope:
    "Choose report reach: all salons, one business, or one salon. Date filtering uses each salon's local accounting date.",
  business_scope: "Limits results to salons under the selected business",
  salon_scope: "Limits results to one salon",
};

const HELP_GUIDE = buildSuperAdminReportHelpGuide({
  reportKey: "tax-liability",
  title: "Tax Liability Summary (Super Admin)",
  subtitle:
    "Review taxable sales, exemptions, and estimated tax totals across the selected scope.",
  summaryLabel: "Tax Summary",
  detailsLabel: "Tax Detail Table",
  hasExport: true,
});

const HELP_ANCHORS = getSuperAdminHelpAnchors("tax-liability");

const formatDateISO = (date, timeZone) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  if (timeZone) {
    try {
      const formatter = new Intl.DateTimeFormat("en-US", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
      const parts = formatter.formatToParts(date);
      const lookup = { year: "", month: "", day: "" };
      for (const part of parts) {
        if (Object.prototype.hasOwnProperty.call(lookup, part.type)) {
          lookup[part.type] = part.value;
        }
      }
      if (lookup.year && lookup.month && lookup.day) {
        return `${lookup.year}-${lookup.month}-${lookup.day}`;
      }
    } catch (err) {
      // Fall back to local time when timezone is invalid or unsupported.
    }
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDateFromParts = (year, month, day) =>
  `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

const addDaysToIsoDate = (dateStr, deltaDays) => {
  if (!dateStr) return "";
  const parts = dateStr.split("-");
  if (parts.length !== 3) return "";
  const [year, month, day] = parts.map((value) => Number(value));
  if (![year, month, day].every((value) => Number.isFinite(value))) return "";
  const base = new Date(Date.UTC(year, month - 1, day));
  base.setUTCDate(base.getUTCDate() + deltaDays);
  return formatDateFromParts(
    base.getUTCFullYear(),
    base.getUTCMonth() + 1,
    base.getUTCDate()
  );
};

const formatYMDInTimeZone = (isoString, timeZone) => {
  if (!isoString) return "";
  const rawValue = typeof isoString === "string" ? isoString.trim() : isoString;
  if (!rawValue) return "";
  const hasTimeZone =
    typeof rawValue === "string" && /([zZ]|[+-]\d{2}:\d{2})$/.test(rawValue);
  // Treat timestamps without a zone designator as UTC from the API.
  const normalizedValue =
    typeof rawValue === "string" && !hasTimeZone ? `${rawValue}Z` : rawValue;
  const dateValue =
    rawValue instanceof Date ? rawValue : new Date(normalizedValue);
  if (Number.isNaN(dateValue.getTime())) return "";
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(dateValue);
    const y = parts.find((part) => part.type === "year")?.value;
    const m = parts.find((part) => part.type === "month")?.value;
    const d = parts.find((part) => part.type === "day")?.value;
    return y && m && d ? `${y}-${m}-${d}` : "";
  } catch (err) {
    return "";
  }
};

const isInvalidDateRange = (startValue, endValue) => {
  if (!startValue || !endValue) return false;
  return endValue < startValue;
};

const formatMoney = (value) => {
  const number = Number(value ?? 0);
  if (Number.isNaN(number)) return "$0.00";
  const fixed = Math.abs(number).toFixed(2);
  return number < 0 ? `-$${fixed}` : `$${fixed}`;
};

const formatPercent = (value) => {
  const number = Number(value ?? 0);
  if (Number.isNaN(number)) return "0.00";
  return number.toFixed(2);
};

const toNumber = (value) => {
  const parsed = Number(value ?? 0);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const coerceEmployeeIds = (ids) => {
  if (!Array.isArray(ids)) return [];
  return ids
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id));
};

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.setAttribute("download", filename);
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.URL.revokeObjectURL(url);
};

const buildEmployeeSummary = (employeeIds, staffMap) => {
  if (!employeeIds || employeeIds.length === 0) return "All";
  if (employeeIds.length === 1) {
    const label = staffMap.get(String(employeeIds[0]));
    return label || "1 selected";
  }
  return `${employeeIds.length} selected`;
};

export default function SuperAdminTaxLiabilitySummary() {
  const navigate = useNavigate();
  const { is_super_admin, salonTimezone: authSalonTimezone } = useAuth();
  const { jumpToSectionWithHighlight, getSectionClassName } = useHelpGuideNavigation();
  const [scope, setScope] = useState("all");
  const [businessId, setBusinessId] = useState("");
  const [salonId, setSalonId] = useState("");
  const [businesses, setBusinesses] = useState([]);
  const [salons, setSalons] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [filtersError, setFiltersError] = useState("");
  const businessMap = useMemo(() => {
    const map = new Map();
    (businesses || []).forEach((item) => {
      if (!item) return;
      map.set(String(item.id), item);
    });
    return map;
  }, [businesses]);
  const salonMap = useMemo(() => {
    const map = new Map();
    (salons || []).forEach((item) => {
      if (!item) return;
      map.set(String(item.id), item);
    });
    return map;
  }, [salons]);
  const anchorTimezone = useMemo(() => {
    if (salonId) {
      const salon = salonMap.get(String(salonId));
      if (salon?.timezone) return salon.timezone;
    }
    if (businessId) {
      const business = businessMap.get(String(businessId));
      if (business?.timezone) return business.timezone;
    }
    return authSalonTimezone || "UTC";
  }, [salonId, salonMap, businessId, businessMap, authSalonTimezone]);
  const baseTimezone = useMemo(
    () =>
      anchorTimezone ||
      authSalonTimezone ||
      "UTC",
    [anchorTimezone, authSalonTimezone]
  );
  const today = useMemo(
    () => formatDateISO(new Date(), baseTimezone),
    [baseTimezone]
  );
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [employeeIds, setEmployeeIds] = useState(["ALL"]);
  const [showTaxExemptItems, setShowTaxExemptItems] = useState(true);
  const [showTaxableItems, setShowTaxableItems] = useState(true);
  const [payByCash, setPayByCash] = useState(false);
  const [dateRangeError, setDateRangeError] = useState("");
  const [summary, setSummary] = useState(null);
  const [detail, setDetail] = useState(null);
  const [activeKind, setActiveKind] = useState("");
  const [loadingKind, setLoadingKind] = useState("");
  const [reportError, setReportError] = useState("");
  const [exportError, setExportError] = useState("");
  const [exporting, setExporting] = useState("");
  const [runFilters, setRunFilters] = useState(null);

  const staffMap = useMemo(() => {
    const map = new Map();
    (employees || []).forEach((item) => {
      if (!item) return;
      const label = item.display_name || item.full_name || `Staff ${item.id}`;
      map.set(String(item.id), label);
    });
    return map;
  }, [employees]);

  useEffect(() => {
    if (!is_super_admin) return;
    let mounted = true;
    const loadFilters = async () => {
      setFiltersLoading(true);
      setFiltersError("");
      try {
        const data = await getSuperAdminTaxLiabilityFilters({
          scope,
          business_id: businessId || undefined,
          salon_id: salonId || undefined,
        });
        if (!mounted) return;
        setBusinesses(Array.isArray(data?.businesses) ? data.businesses : []);
        setSalons(Array.isArray(data?.salons) ? data.salons : []);
        setEmployees(Array.isArray(data?.employees) ? data.employees : []);
      } catch (error) {
        if (!mounted) return;
        setBusinesses([]);
        setSalons([]);
        setEmployees([]);
        setFiltersError("Unable to load scope filters.");
      } finally {
        if (mounted) {
          setFiltersLoading(false);
        }
      }
    };

    loadFilters();

    return () => {
      mounted = false;
    };
  }, [is_super_admin, scope, businessId, salonId]);

  useEffect(() => {
    if (isInvalidDateRange(startDate, endDate)) {
      setDateRangeError("End date can't be before start date.");
    } else {
      setDateRangeError("");
    }
  }, [startDate, endDate]);

  const selectedEmployeeIds = useMemo(
    () => (employeeIds.includes("ALL") ? [] : coerceEmployeeIds(employeeIds)),
    [employeeIds]
  );

  const employeeSummary = useMemo(() => {
    if (!runFilters) return "";
    return buildEmployeeSummary(runFilters.employee_ids, staffMap);
  }, [runFilters, staffMap]);

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    navigate("/superadmin/reports", { replace: true });
  };
  const renderFieldLabel = (label, tooltipKey) => (
    <div className="flex items-center gap-1.5">
      <label className={LABEL_TEXT}>{label}</label>
      {tooltipKey ? (
        <InfoTooltip text={SUPERADMIN_TAX_TOOLTIPS[tooltipKey]} label={label} />
      ) : null}
    </div>
  );

  const handleEmployeeChange = (event) => {
    const values = Array.from(event.target.selectedOptions).map(
      (option) => option.value
    );
    if (values.includes("ALL") || values.length === 0) {
      setEmployeeIds(["ALL"]);
      return;
    }
    setEmployeeIds(values);
  };

  const handleScopeChange = (nextScope) => {
    setScope(nextScope);
    if (nextScope === "all") {
      setSalonId("");
      return;
    }
    if (nextScope === "business") {
      setSalonId("");
    }
  };

  const handleBusinessChange = (nextBusinessId) => {
    setBusinessId(nextBusinessId);
    if (!nextBusinessId && scope !== "salon") {
      setSalonId("");
      return;
    }
    if (salonId) {
      const selectedSalon = salonMap.get(String(salonId));
      if (
        selectedSalon &&
        nextBusinessId &&
        String(selectedSalon.business_id || "") !== String(nextBusinessId)
      ) {
        setSalonId("");
      }
    }
  };

  const handleSalonChange = (nextSalonId) => {
    setSalonId(nextSalonId);
    if (!nextSalonId) return;
    const selectedSalon = salonMap.get(String(nextSalonId));
    if (selectedSalon?.business_id) {
      setBusinessId(String(selectedSalon.business_id));
    }
  };

  const effectiveShowTaxExempt = useMemo(() => {
    return showTaxExemptItems || (!showTaxExemptItems && !showTaxableItems);
  }, [showTaxExemptItems, showTaxableItems]);

  const effectiveShowTaxable = useMemo(() => {
    return showTaxableItems || (!showTaxExemptItems && !showTaxableItems);
  }, [showTaxableItems, showTaxExemptItems]);

  const buildSummaryPayload = () => ({
    scope,
    business_id: businessId ? Number(businessId) : undefined,
    salon_id: salonId ? Number(salonId) : undefined,
    start_date: startDate,
    end_date: endDate,
    employee_ids: selectedEmployeeIds.length > 0 ? selectedEmployeeIds : null,
    show_exempt: effectiveShowTaxExempt,
    show_taxable: effectiveShowTaxable,
    paid_by_cash: payByCash,
  });

  const buildDetailPayload = () => ({
    ...buildSummaryPayload(),
  });

  const handleRunSummary = async () => {
    if (isInvalidDateRange(startDate, endDate)) {
      setDateRangeError("End date can't be before start date.");
      return;
    }
    if (scope === "business" && !businessId) {
      setReportError("Choose a business for Business scope.");
      return;
    }
    if (scope === "salon" && !salonId) {
      setReportError("Choose a salon for Salon scope.");
      return;
    }
    setReportError("");
    setLoadingKind("summary");
    setActiveKind("summary");
    try {
      const payload = buildSummaryPayload();
      const data = await postSuperAdminTaxLiabilitySummary(payload);
      setSummary(data);
      setRunFilters({ kind: "summary", ...payload });
    } catch (error) {
      setReportError("Unable to load summary report.");
      setSummary(null);
    } finally {
      setLoadingKind("");
    }
  };

  const handleRunDetail = async () => {
    if (isInvalidDateRange(startDate, endDate)) {
      setDateRangeError("End date can't be before start date.");
      return;
    }
    if (scope === "business" && !businessId) {
      setReportError("Choose a business for Business scope.");
      return;
    }
    if (scope === "salon" && !salonId) {
      setReportError("Choose a salon for Salon scope.");
      return;
    }
    setReportError("");
    setLoadingKind("detail");
    setActiveKind("detail");
    try {
      const payload = buildDetailPayload();
      const data = await postSuperAdminTaxLiabilityDetail(payload);
      setDetail(data);
      setRunFilters({ kind: "detail", ...payload });
    } catch (error) {
      setReportError("Unable to load detail report.");
      setDetail(null);
    } finally {
      setLoadingKind("");
    }
  };

  const handleExport = async (kind, format) => {
    if (isInvalidDateRange(startDate, endDate)) {
      setDateRangeError("End date can't be before start date.");
      return;
    }
    if (scope === "business" && !businessId) {
      setExportError("Choose a business for Business scope.");
      return;
    }
    if (scope === "salon" && !salonId) {
      setExportError("Choose a salon for Salon scope.");
      return;
    }
    setExportError("");
    const exportKey = `${kind}-${format}`;
    setExporting(exportKey);

    let targetWindow = null;
    if (format === "pdf") {
      targetWindow = window.open("about:blank", "_blank");
      if (!targetWindow) {
        setExportError(
          "Pop-up blocked. Please allow pop-ups for this site to open the report."
        );
        setExporting("");
        return;
      }
    }

    let payload = null;
    if (kind === "detail") {
      payload = runFilters?.kind === "detail" ? runFilters : buildDetailPayload();
    } else {
      payload =
        runFilters?.kind === "summary" ? runFilters : buildSummaryPayload();
    }

    try {
      const exportPayload = { ...payload, format };
      const blob =
        kind === "detail"
          ? await exportSuperAdminTaxLiabilityDetail(exportPayload)
          : await exportSuperAdminTaxLiabilitySummary(exportPayload);
      if (String(format).toLowerCase() === "pdf") {
        const url = window.URL.createObjectURL(blob);
        if (targetWindow && !targetWindow.closed) {
          targetWindow.location.href = url;
        } else {
          window.open(url, "_blank");
        }
        window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
      } else {
        const fileStart = payload.start_date || startDate;
        const fileEnd = payload.end_date || endDate;
        downloadBlob(
          blob,
          `superadmin_tax_liability_${kind}_${fileStart}_to_${fileEnd}.csv`
        );
      }
    } catch (error) {
      if (targetWindow && !targetWindow.closed) {
        targetWindow.document.title = "Report Error";
        targetWindow.location.href = "about:blank";
      }
      setExportError("Unable to export report.");
    } finally {
      setExporting("");
    }
  };

  const summarySections = summary?.sections || [];
  const visibleSummarySections = summarySections.filter(
    (section) =>
      section.key !== "packages_memberships" && section.key !== "ticket_fees"
  );
  const detailRows = detail?.rows || [];
  const detailTimezone = detail?.meta?.timezone;
  const reportTimezone = detailTimezone || baseTimezone || "UTC";
  const showTaxableCol = effectiveShowTaxable;
  const showNontaxableCol = effectiveShowTaxExempt;
  const hideNontaxable = !showNontaxableCol;
  const hideTaxable = !showTaxableCol;
  const detailColumnCount =
    9 + (showTaxableCol ? 1 : 0) + (showNontaxableCol ? 1 : 0);
  const filteredDetailRows = useMemo(() => {
    return detailRows.filter((row) => {
      const taxable = toNumber(row.taxable_total);
      const nontaxable = toNumber(row.nontaxable_total);
      const isTaxableRow = taxable !== 0;
      const isExemptRow = nontaxable !== 0;

      if (showTaxableCol && showNontaxableCol) {
        return isTaxableRow || isExemptRow;
      }
      if (showTaxableCol) return isTaxableRow;
      if (showNontaxableCol) return isExemptRow;
      return isTaxableRow || isExemptRow;
    });
  }, [detailRows, showTaxableCol, showNontaxableCol]);

  const detailColumns = useMemo(
    () => ({
      date: {
        type: "date",
        getValue: (row) => row?.ticket_closed_at || row?.closed_at || row?.date,
      },
      employee: { type: "text", getValue: (row) => row?.employee || "" },
      client: { type: "text", getValue: (row) => row?.client || "" },
      service: { type: "text", getValue: (row) => row?.service || "" },
      price: { type: "number", getValue: (row) => row?.price },
      discount: { type: "number", getValue: (row) => row?.discount },
      taxable_total: { type: "number", getValue: (row) => row?.taxable_total },
      nontaxable_total: { type: "number", getValue: (row) => row?.nontaxable_total },
      total: { type: "number", getValue: (row) => row?.total },
      tax_percent: { type: "percent", getValue: (row) => row?.tax_percent },
      tax_amount: { type: "number", getValue: (row) => row?.tax_amount },
    }),
    []
  );

  const detailSort = useTableSort({
    rows: filteredDetailRows,
    columns: detailColumns,
    fallbackKey: "date",
  });

  const detailTotals = useMemo(() => {
    return filteredDetailRows.reduce(
      (acc, row) => ({
        price: acc.price + toNumber(row.price),
        discount: acc.discount + toNumber(row.discount),
        taxable: acc.taxable + toNumber(row.taxable_total),
        nontaxable: acc.nontaxable + toNumber(row.nontaxable_total),
        total: acc.total + toNumber(row.total),
        tax: acc.tax + toNumber(row.tax_amount),
      }),
      {
        price: 0,
        discount: 0,
        taxable: 0,
        nontaxable: 0,
        total: 0,
        tax: 0,
      }
    );
  }, [filteredDetailRows]);

  useEffect(() => {
    if (activeKind === "detail" && detail) {
      handleRunDetail();
    }
    if (activeKind === "summary" && summary) {
      handleRunSummary();
    }
  }, [effectiveShowTaxExempt, effectiveShowTaxable, payByCash]);

  const setRange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
  };

  const setTodayRange = () => setRange(today, today);
  const setLast7DaysRange = () => {
    const start = addDaysToIsoDate(today, -6);
    setRange(start, today);
  };
  const setThisMonthRange = () => {
    const parts = today.split("-");
    if (parts.length !== 3) return;
    const start = formatDateFromParts(parts[0], parts[1], 1);
    setRange(start, today);
  };
  const setLastMonthRange = () => {
    const year = Number(today.slice(0, 4));
    const month = Number(today.slice(5, 7));
    if (!Number.isFinite(year) || !Number.isFinite(month)) return;
    let prevYear = year;
    let prevMonth = month - 1;
    if (prevMonth < 1) {
      prevMonth = 12;
      prevYear -= 1;
    }
    const endDay = new Date(Date.UTC(prevYear, prevMonth, 0)).getUTCDate();
    const start = formatDateFromParts(prevYear, prevMonth, 1);
    const end = formatDateFromParts(prevYear, prevMonth, endDay);
    setRange(start, end);
  };
  const setThisYearRange = () => {
    const year = Number(today.slice(0, 4));
    if (!Number.isFinite(year)) return;
    setRange(formatDateFromParts(year, 1, 1), today);
  };
  const setLastYearRange = () => {
    const year = Number(today.slice(0, 4)) - 1;
    if (!Number.isFinite(year)) return;
    setRange(formatDateFromParts(year, 1, 1), formatDateFromParts(year, 12, 31));
  };

  const filteredSalons = useMemo(() => {
    if (!businessId) return salons;
    return (salons || []).filter(
      (salon) => String(salon.business_id || "") === String(businessId)
    );
  }, [salons, businessId]);

  if (!is_super_admin) {
    return (
      <div className="reportSectionShell min-h-screen bg-slate-100">
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          Super Admin access required.
        </div>
      </div>
    );
  }

  return (
    <div className="reportSectionShell min-h-screen bg-slate-100">
      <div className="reportSectionWide report-section-spacious report-section-compact">
        <div className="rounded-3xl bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4 flex items-center justify-between gap-4 relative">
            <div className="text-xl md:text-2xl font-semibold text-white">
              Tax Liability Summary
            </div>
            <div
              className="absolute right-4 top-3 z-50"
              onClick={handleClose}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <ModalCloseX onClick={handleClose} />
            </div>

          </div>

          <div className={`px-6 py-6 space-y-6 ${MODAL_TEXT}`}>
            <div
              id={HELP_ANCHORS.overview}
              className={`rounded-2xl ${getSectionClassName(HELP_ANCHORS.overview)}`}
            >
              <ReportHelpCard
                title={HELP_GUIDE.title}
                subtitle={HELP_GUIDE.subtitle}
                sections={HELP_GUIDE.sections}
                troubleshooting={HELP_GUIDE.troubleshooting}
                onJumpToSection={jumpToSectionWithHighlight}
              />
            </div>
            <div
              id={HELP_ANCHORS.filters}
              className={`rounded-2xl border border-slate-200 bg-slate-50 p-5 ${getSectionClassName(
                HELP_ANCHORS.filters
              )}`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="text-base md:text-lg font-semibold text-slate-900">
                  Filters
                </div>
                <button
                  type="button"
                  onClick={() => setFiltersOpen((prev) => !prev)}
                  className="text-rose-600 font-semibold hover:text-rose-700"
                >
                  [{filtersOpen ? "hide filters" : "show filters"}]
                </button>
              </div>

              {filtersOpen && (
                <div className="mt-4 space-y-4">
                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="flex flex-col gap-1">
                      {renderFieldLabel("Scope", "scope")}
                      <select
                        value={scope}
                        onChange={(event) => handleScopeChange(event.target.value)}
                        className={FIELD_CLASS}
                        disabled={filtersLoading}
                      >
                        {SCOPE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex flex-col gap-1">
                      {renderFieldLabel("Business", "business_scope")}
                      <select
                        value={businessId}
                        onChange={(event) => handleBusinessChange(event.target.value)}
                        className={FIELD_CLASS}
                        disabled={filtersLoading}
                      >
                        <option value="">All Businesses</option>
                        {(businesses || []).map((business) => (
                          <option key={business.id} value={String(business.id)}>
                            {business.code} - {business.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex flex-col gap-1">
                      {renderFieldLabel("Salon", "salon_scope")}
                      <select
                        value={salonId}
                        onChange={(event) => handleSalonChange(event.target.value)}
                        className={FIELD_CLASS}
                        disabled={
                          filtersLoading ||
                          !(
                            scope === "salon" ||
                            scope === "business" ||
                            Boolean(businessId)
                          )
                        }
                      >
                        <option value="">
                          {scope === "salon" ? "Select Salon" : "All Salons"}
                        </option>
                        {filteredSalons.map((salon) => (
                          <option key={salon.id} value={String(salon.id)}>
                            {salon.name} (ID {salon.id})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="flex flex-col gap-1">
                      {renderFieldLabel("Start Date", "start_date")}
                      <input
                        type="date"
                        value={startDate}
                        onChange={(event) => setStartDate(event.target.value)}
                        className={FIELD_CLASS}
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      {renderFieldLabel("End Date", "end_date")}
                      <input
                        type="date"
                        value={endDate}
                        onChange={(event) => setEndDate(event.target.value)}
                        className={FIELD_CLASS}
                      />
                    </div>
                  </div>
                  {filtersError && (
                    <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm md:text-base text-rose-700">
                      {filtersError}
                    </div>
                  )}
                  {dateRangeError && (
                    <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm md:text-base text-rose-700">
                      {dateRangeError}
                    </div>
                  )}

                  <div className="flex flex-wrap items-center gap-3 text-sm md:text-base">
                    <span className="text-slate-500">Quick pick:</span>
                    <button
                      type="button"
                      onClick={setTodayRange}
                      className="text-rose-600 font-semibold hover:text-rose-700"
                    >
                      Today
                    </button>
                    <button
                      type="button"
                      onClick={setLast7DaysRange}
                      className="text-rose-600 font-semibold hover:text-rose-700"
                    >
                      Last 7 days
                    </button>
                    <button
                      type="button"
                      onClick={setThisMonthRange}
                      className="text-rose-600 font-semibold hover:text-rose-700"
                    >
                      This month
                    </button>
                    <button
                      type="button"
                      onClick={setLastMonthRange}
                      className="text-rose-600 font-semibold hover:text-rose-700"
                    >
                      Last month
                    </button>
                    <button
                      type="button"
                      onClick={setThisYearRange}
                      className="text-rose-600 font-semibold hover:text-rose-700"
                    >
                      This year
                    </button>
                    <button
                      type="button"
                      onClick={setLastYearRange}
                      className="text-rose-600 font-semibold hover:text-rose-700"
                    >
                      Last year
                    </button>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="flex flex-col gap-1">
                      {renderFieldLabel("Employee", "employee")}
                      <select
                        multiple
                        size={Math.max(4, Math.min(8, (employees || []).length + 1))}
                        value={employeeIds}
                        onChange={handleEmployeeChange}
                        className={`${FIELD_CLASS} h-auto min-h-[160px] py-2`}
                      >
                        <option value="ALL">* ALL *</option>
                        {(employees || []).map((item) => (
                          <option key={item.id} value={String(item.id)}>
                            {item.display_name || item.full_name || `Staff ${item.id}`}
                          </option>
                        ))}
                      </select>
                      {filtersLoading && (
                        <div className="text-sm md:text-base text-slate-500">
                          Loading employees...
                        </div>
                      )}
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-start gap-2 text-sm md:text-base text-slate-700">
                        <input
                          type="checkbox"
                          checked={showTaxExemptItems}
                          onChange={(event) =>
                            setShowTaxExemptItems(event.target.checked)
                          }
                          className="mt-1 h-4 w-4 rounded border-slate-300 text-rose-600"
                        />
                        <div className="flex items-center gap-1.5">
                          <span>Show tax-exempt items</span>
                          <InfoTooltip
                            text={SUPERADMIN_TAX_TOOLTIPS.show_tax_exempt}
                            label="Show tax-exempt items"
                          />
                        </div>
                      </div>
                      <div className="flex items-start gap-2 text-sm md:text-base text-slate-700">
                        <input
                          type="checkbox"
                          checked={showTaxableItems}
                          onChange={(event) =>
                            setShowTaxableItems(event.target.checked)
                          }
                          className="mt-1 h-4 w-4 rounded border-slate-300 text-rose-600"
                        />
                        <div className="flex items-center gap-1.5">
                          <span>Show taxable items</span>
                          <InfoTooltip
                            text={SUPERADMIN_TAX_TOOLTIPS.show_taxable}
                            label="Show taxable items"
                          />
                        </div>
                      </div>
                      <div className="flex items-start gap-2 text-sm md:text-base text-slate-700">
                        <input
                          type="checkbox"
                          checked={payByCash}
                          onChange={(event) => setPayByCash(event.target.checked)}
                          className="mt-1 h-4 w-4 rounded border-slate-300 text-rose-600"
                        />
                        <div className="flex items-center gap-1.5">
                          <span>Paid by cash</span>
                          <InfoTooltip
                            text={SUPERADMIN_TAX_TOOLTIPS.pay_by_cash}
                            label="Paid by cash"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={handleRunDetail}
                      className={BTN_PRIMARY}
                      disabled={loadingKind === "detail"}
                    >
                      {loadingKind === "detail" ? "Loading..." : "Run Detail Report"}
                    </button>
                    <button
                      type="button"
                      onClick={handleRunSummary}
                      className={BTN_OUTLINE}
                      disabled={loadingKind === "summary"}
                    >
                      {loadingKind === "summary" ? "Loading..." : "Run Summary Report"}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div
              id={HELP_ANCHORS.export}
              className={`rounded-2xl border border-slate-200 bg-white p-4 ${getSectionClassName(
                HELP_ANCHORS.export
              )}`}
            >
              <div className="text-base md:text-lg font-semibold text-slate-900">Export</div>
              <div className="mt-1 text-sm md:text-base text-slate-600">
                Export buttons appear in the Detail or Summary report header once results are
                loaded.
              </div>
            </div>

            {reportError && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-sm md:text-base">
                {reportError}
              </div>
            )}

            {runFilters && (
              <div
                id={HELP_ANCHORS.kpis}
                className={`rounded-2xl border border-emerald-200 bg-emerald-50/70 px-4 py-3 text-sm md:text-base text-emerald-700 ${getSectionClassName(
                  HELP_ANCHORS.kpis
                )}`}
              >
                <div className="flex flex-wrap gap-4">
                  <span>
                    <span className="font-semibold">Scope:</span>{" "}
                    {runFilters.scope === "business"
                      ? "Business"
                      : runFilters.scope === "salon"
                      ? "Salon"
                      : "All Salons"}
                  </span>
                  <span>
                    <span className="font-semibold">Start:</span>{" "}
                    {runFilters.start_date}
                  </span>
                  <span>
                    <span className="font-semibold">End:</span> {runFilters.end_date}
                  </span>
                  <span>
                    <span className="font-semibold">Business:</span>{" "}
                    {runFilters.business_id
                      ? businessMap.get(String(runFilters.business_id))?.name ||
                        `Business ${runFilters.business_id}`
                      : "All"}
                  </span>
                  <span>
                    <span className="font-semibold">Salon:</span>{" "}
                    {runFilters.salon_id
                      ? salonMap.get(String(runFilters.salon_id))?.name ||
                        `Salon ${runFilters.salon_id}`
                      : "All"}
                  </span>
                  <span>
                    <span className="font-semibold">Employee:</span> {employeeSummary}
                  </span>
                  <span>
                    <span className="font-semibold">Show Tax-Exempt:</span>{" "}
                    {runFilters.show_exempt ? "Yes" : "No"}
                  </span>
                  {typeof runFilters.show_taxable === "boolean" && (
                    <span>
                      <span className="font-semibold">Show Taxable:</span>{" "}
                      {runFilters.show_taxable ? "Yes" : "No"}
                    </span>
                  )}
                  {typeof runFilters.paid_by_cash === "boolean" && (
                    <span>
                      <span className="font-semibold">Paid by cash:</span>{" "}
                      {runFilters.paid_by_cash ? "Yes" : "No"}
                    </span>
                  )}
                </div>
              </div>
            )}

            {exportError && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-sm md:text-base">
                {exportError}
              </div>
            )}

            <div
              id={HELP_ANCHORS.details}
              className={`space-y-4 rounded-2xl ${getSectionClassName(HELP_ANCHORS.details)}`}
            >
              {activeKind === "detail" && detail && (
                <div className="space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="text-lg md:text-xl font-semibold text-slate-900">
                      Detail Report
                    </div>
                    <div className="text-sm md:text-base text-slate-600">
                      {detail?.meta?.start} to {detail?.meta?.end}
                    </div>
                    {payByCash && detail?.meta?.cash_collected && (
                      <div className="text-sm md:text-base text-emerald-700">
                        Cash Collected: {formatMoney(detail?.meta?.cash_collected)}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={() => handleExport("detail", "csv")}
                      className={BTN_OUTLINE}
                      disabled={exporting === "detail-csv"}
                    >
                      {exporting === "detail-csv" ? "Exporting..." : "Export to Excel"}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleExport("detail", "pdf")}
                      className={BTN_OUTLINE}
                      disabled={exporting === "detail-pdf"}
                    >
                      {exporting === "detail-pdf" ? "Exporting..." : "Export to PDF"}
                    </button>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full table-fixed text-[10px] sm:text-[11px] md:text-xs">
                      <colgroup>
                        <col className="w-[84px]" />
                        <col className="w-[96px]" />
                        <col className="w-[120px]" />
                        <col className="w-auto" />
                        <col className="w-[76px]" />
                        <col className="w-[76px]" />
                        {!hideTaxable && <col className="w-[92px]" />}
                        {!hideNontaxable && <col className="w-[110px]" />}
                        <col className="w-[76px]" />
                        <col className="w-[56px]" />
                        <col className="w-[76px]" />
                      </colgroup>
                      <thead className="bg-slate-50 text-slate-600">
                        <tr>
                          <SortableTh
                            label="Date"
                            sortKey="date"
                            sortState={detailSort.sortState}
                            onSort={detailSort.handleSort}
                            tooltip={TAX_LIABILITY_TOOLTIPS.column_date}
                            className="px-2 py-1.5"
                            align="left"
                          />
                          <SortableTh
                            label="Employee"
                            sortKey="employee"
                            sortState={detailSort.sortState}
                            onSort={detailSort.handleSort}
                            tooltip={TAX_LIABILITY_TOOLTIPS.column_employee}
                            className="px-2 py-1.5"
                            align="left"
                          />
                          <SortableTh
                            label="Client"
                            sortKey="client"
                            sortState={detailSort.sortState}
                            onSort={detailSort.handleSort}
                            tooltip={TAX_LIABILITY_TOOLTIPS.column_client}
                            className="px-2 py-1.5"
                            align="left"
                          />
                          <SortableTh
                            label="Service"
                            sortKey="service"
                            sortState={detailSort.sortState}
                            onSort={detailSort.handleSort}
                            tooltip={TAX_LIABILITY_TOOLTIPS.column_service}
                            className="px-2 py-1.5"
                            align="left"
                          />
                          <SortableTh
                            label="Price"
                            sortKey="price"
                            sortState={detailSort.sortState}
                            onSort={detailSort.handleSort}
                            tooltip={TAX_LIABILITY_TOOLTIPS.column_price}
                            className="px-2 py-1.5"
                            align="right"
                          />
                          <SortableTh
                            label="Discount"
                            sortKey="discount"
                            sortState={detailSort.sortState}
                            onSort={detailSort.handleSort}
                            tooltip={TAX_LIABILITY_TOOLTIPS.column_discount}
                            className="px-2 py-1.5"
                            align="right"
                          />
                          {!hideTaxable && (
                            <SortableTh
                              label="Taxable Total"
                              sortKey="taxable_total"
                              sortState={detailSort.sortState}
                              onSort={detailSort.handleSort}
                              tooltip={TAX_LIABILITY_TOOLTIPS.column_taxable_total}
                              className="px-2 py-1.5"
                              align="right"
                            />
                          )}
                          {!hideNontaxable && (
                            <SortableTh
                              label="Nontaxable Total"
                              sortKey="nontaxable_total"
                              sortState={detailSort.sortState}
                              onSort={detailSort.handleSort}
                              tooltip={TAX_LIABILITY_TOOLTIPS.column_nontaxable_total}
                              className="px-2 py-1.5"
                              align="right"
                            />
                          )}
                          <SortableTh
                            label="Total"
                            sortKey="total"
                            sortState={detailSort.sortState}
                            onSort={detailSort.handleSort}
                            tooltip={TAX_LIABILITY_TOOLTIPS.column_total}
                            className="px-2 py-1.5"
                            align="right"
                          />
                          <SortableTh
                            label="Tax %"
                            sortKey="tax_percent"
                            sortState={detailSort.sortState}
                            onSort={detailSort.handleSort}
                            tooltip={TAX_LIABILITY_TOOLTIPS.column_tax_percent}
                            className="px-2 py-1.5"
                            align="right"
                          />
                          <SortableTh
                            label="Tax $"
                            sortKey="tax_amount"
                            sortState={detailSort.sortState}
                            onSort={detailSort.handleSort}
                            tooltip={TAX_LIABILITY_TOOLTIPS.column_tax_amount}
                            className="px-2 py-1.5"
                            align="right"
                          />
                        </tr>
                      </thead>
                      <tbody>
                        {detailSort.sortedRows.length === 0 ? (
                          <tr>
                            <td
                              colSpan={detailColumnCount}
                              className="px-2 py-3 text-slate-500"
                            >
                              No line items found for this range.
                            </td>
                          </tr>
                        ) : (
                          detailSort.sortedRows.map((row, idx) => (
                            <tr key={`${row.date}-${row.service}-${idx}`} className="border-t">
                              <td className="px-2 py-2 text-slate-700 truncate max-w-0">
                                {formatYMDInTimeZone(
                                  row.ticket_closed_at || row.closed_at,
                                  reportTimezone
                                ) || row.date}
                              </td>
                              <td className="px-2 py-2 text-slate-700 truncate max-w-0">
                                {row.employee || "Unassigned"}
                              </td>
                              <td className="px-2 py-2 text-slate-700 truncate max-w-0">
                                {row.client || "Walk-in"}
                              </td>
                              <td className="px-2 py-2 text-slate-700 truncate max-w-0">
                                {row.service}
                              </td>
                              <td className="px-2 py-2 text-slate-700 text-right whitespace-nowrap tabular-nums">
                                {formatMoney(row.price)}
                              </td>
                              <td className="px-2 py-2 text-slate-700 text-right whitespace-nowrap tabular-nums">
                                {formatMoney(row.discount)}
                              </td>
                              {!hideTaxable && (
                                <td className="px-2 py-2 text-slate-700 text-right whitespace-nowrap tabular-nums">
                                  {formatMoney(row.taxable_total)}
                                </td>
                              )}
                              {!hideNontaxable && (
                                <td className="px-2 py-2 text-slate-700 text-right whitespace-nowrap tabular-nums">
                                  {formatMoney(row.nontaxable_total)}
                                </td>
                              )}
                              <td className="px-2 py-2 text-slate-700 text-right whitespace-nowrap tabular-nums">
                                {formatMoney(row.total)}
                              </td>
                              <td className="px-2 py-2 text-slate-700 text-right whitespace-nowrap tabular-nums">
                                {formatPercent(row.tax_percent)}
                              </td>
                              <td className="px-2 py-2 text-slate-700 text-right whitespace-nowrap tabular-nums">
                                {formatMoney(row.tax_amount)}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                      {filteredDetailRows.length > 0 && (
                        <tfoot className="bg-slate-50 font-semibold text-slate-700 border-t border-slate-200">
                          <tr>
                            <td colSpan={4} className="px-2 py-1.5 text-right pr-3">
                              TOTALS:
                            </td>
                            <td className="px-2 py-1.5 text-right whitespace-nowrap tabular-nums">
                              {formatMoney(detailTotals.price)}
                            </td>
                            <td className="px-2 py-1.5 text-right whitespace-nowrap tabular-nums">
                              {formatMoney(detailTotals.discount)}
                            </td>
                            {!hideTaxable && (
                              <td className="px-2 py-1.5 text-right whitespace-nowrap tabular-nums">
                                {formatMoney(detailTotals.taxable)}
                              </td>
                            )}
                            {!hideNontaxable && (
                              <td className="px-2 py-1.5 text-right whitespace-nowrap tabular-nums">
                                {formatMoney(detailTotals.nontaxable)}
                              </td>
                            )}
                            <td className="px-2 py-1.5 text-right whitespace-nowrap tabular-nums">
                              {formatMoney(detailTotals.total)}
                            </td>
                            <td className="px-2 py-1.5 text-right whitespace-nowrap tabular-nums">
                              --
                            </td>
                            <td className="px-2 py-1.5 text-right whitespace-nowrap tabular-nums">
                              {formatMoney(detailTotals.tax)}
                            </td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                </div>
              </div>
              )}

              {activeKind === "summary" && summary && (
                <div className="space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="text-lg md:text-xl font-semibold text-slate-900">
                      Summary Report
                    </div>
                    <div className="text-sm md:text-base text-slate-600">
                      {summary?.meta?.start} to {summary?.meta?.end}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={() => handleExport("summary", "csv")}
                      className={BTN_OUTLINE}
                      disabled={exporting === "summary-csv"}
                    >
                      {exporting === "summary-csv" ? "Exporting..." : "Export to Excel"}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleExport("summary", "pdf")}
                      className={BTN_OUTLINE}
                      disabled={exporting === "summary-pdf"}
                    >
                      {exporting === "summary-pdf" ? "Exporting..." : "Export to PDF"}
                    </button>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="text-sm md:text-base text-slate-500">
                    Total Tax Amount
                  </div>
                  <div className="mt-1 text-2xl md:text-3xl font-semibold text-emerald-600">
                    All {formatMoney(summary?.all_tax_amount)}
                  </div>
                  {payByCash && summary?.meta?.cash_collected && (
                    <div className="mt-2 text-sm md:text-base text-emerald-700">
                      Cash Collected: {formatMoney(summary?.meta?.cash_collected)}
                    </div>
                  )}
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  {visibleSummarySections.length === 0 ? (
                    <div className="text-sm md:text-base text-slate-500">
                      No summary data available.
                    </div>
                  ) : (
                    visibleSummarySections.map((section) => (
                        <div
                          key={section.key}
                          className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
                        >
                          <div className="text-base md:text-lg font-semibold text-slate-900">
                            {section.label}
                          </div>
                          <div className="mt-3 space-y-2 text-sm md:text-base">
                            <div className="flex items-center justify-between">
                              <span className="text-slate-600">Item Count</span>
                              <span className="font-semibold text-slate-900">
                                {section.item_count}
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-slate-600">Gross Amount</span>
                              <span className="font-semibold text-slate-900">
                                {formatMoney(section.gross_amount)}
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-slate-600">Discount Amount</span>
                              <span className="font-semibold text-slate-900">
                                {formatMoney(section.discount_amount)}
                              </span>
                            </div>
                            {!hideTaxable && (
                              <div className="flex items-center justify-between">
                                <span className="text-slate-600">Taxable Amount</span>
                                <span className="font-semibold text-slate-900">
                                  {formatMoney(section.taxable_amount)}
                                </span>
                              </div>
                            )}
                            {!hideNontaxable && (
                              <div className="flex items-center justify-between">
                                <span className="text-slate-600">Nontaxable Amount</span>
                                <span className="font-semibold text-slate-900">
                                  {formatMoney(section.nontaxable_amount)}
                                </span>
                              </div>
                            )}
                            <div className="flex items-center justify-between">
                              <span className="text-slate-600">Net Amount</span>
                              <span className="font-semibold text-slate-900">
                                {formatMoney(section.net_amount)}
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-slate-600">Tax Amount</span>
                              <span className="font-semibold text-slate-900">
                                {formatMoney(section.tax_amount)}
                              </span>
                            </div>
                          </div>
                        </div>
                      )
                    )
                  )}
                </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
